import React, { useState } from "react";
import toast from "react-hot-toast";
import { Printer } from "react-feather";
import { useLazyGetAlarmPhotoQuery, useLazyGetAlarmResponseQuery } from "../alarmsApi";
import {
  ALARM_STATUS_LABEL, CAUSE_LABEL, INCIDENT_LABEL, formatAccuracy, formatDateTime, formatDistance, formatDuration,
  isLowAccuracy,
} from "../alarmFormat";
import { PRINT_BASE, PRINT_TD, PrintHeader, blobToDataUrl, printElement } from "../../../components/common/printSheet";

// Position plus how far it can be trusted; a low-accuracy fix is called out in words
// because the printed distance next to it is not evidence.
const coords = (lat, lng, accuracy) => {
  if (lat == null) return "No GPS";
  const acc = accuracy == null ? "" : isLowAccuracy(accuracy) ? ` (LOW GPS ${formatAccuracy(accuracy)})` : ` (${formatAccuracy(accuracy)})`;
  return `${lat}, ${lng}${acc}`;
};

const Row = ({ label, children }) => (
  <tr>
    <td style={{ ...PRINT_TD, width: "130px", fontWeight: 700 }}>{label}</td>
    <td style={PRINT_TD}>{children}</td>
  </tr>
);

// The single-alarm sheet: everything the control room knows about one response,
// photos included, on one or two A4 pages.
const AlarmSheet = ({ detail, photoUrls }) => {
  const r = detail.response;
  const crew = detail.crew.map((c) => `${[c.empName, c.empLastName].filter(Boolean).join(" ")} (${c.empCode})`).join(", ");
  const section = (title) => (
    <div style={{ fontSize: "12px", fontWeight: 700, margin: "10px 0 4px", borderBottom: "1px solid #000" }}>{title}</div>
  );

  return (
    <div style={{ ...PRINT_BASE, padding: "6px" }}>
      <PrintHeader
        title={`Rapid Response ${r.responseNumber}`}
        subtitle={`${INCIDENT_LABEL[r.incidentType] || r.incidentType} · ${ALARM_STATUS_LABEL[r.status] || r.status}`}
        lines={[r.alarmCause ? `Cause: ${CAUSE_LABEL[r.alarmCause]}${r.causeRemarks ? ` - ${r.causeRemarks}` : ""}` : null]}
        branchCode={r.branchCode}
      />
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <Row label="Customer">{r.customerName}{r.customerPhone ? ` · ${r.customerPhone}` : ""}</Row>
          <Row label="Site">{[r.siteLabel, r.siteAddress].filter(Boolean).join(" - ") || "—"} · Pin {coords(r.customerLatitude, r.customerLongitude)}</Row>
          <Row label="Incident">{r.incidentDescription}</Row>
          <Row label="Unit">{r.unitName}{r.vehicleRegNo ? ` (${r.vehicleRegNo})` : ""}</Row>
          <Row label="Attended by">{crew || "—"}</Row>
        </tbody>
      </table>

      {section("Times and locations")}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <Row label="Signal received">{formatDateTime(r.signalReceivedAt)}</Row>
          <Row label="Dispatched">{formatDateTime(r.dispatchedDate)} · {r.dispatchedByName}</Row>
          <Row label="Opened (start)">
            {r.openedDate ? `${formatDateTime(r.openedDate)} · ${coords(r.openedLatitude, r.openedLongitude, r.openedAccuracyMeters)} · ${formatDistance(r.startToCustomerMeters)} from site` : "—"}
          </Row>
          <Row label="Arrived">
            {r.arrivedDate
              ? `${formatDateTime(r.arrivedDate)} · ${coords(r.arrivedLatitude, r.arrivedLongitude, r.arrivedAccuracyMeters)} · ${formatDistance(r.arrivalToCustomerMeters)} from pin · ${r.arrivedQrVerified ? "QR scanned" : "no QR"}`
              : "—"}
          </Row>
          <Row label="Reported">{r.reportedDate ? `${formatDateTime(r.reportedDate)} · ${coords(r.reportedLatitude, r.reportedLongitude, r.reportedAccuracyMeters)}` : "—"}</Row>
          <Row label="Time taken">
            Response {formatDuration(r.responseSeconds)} · Travel {formatDuration(r.travelSeconds)} · On site{" "}
            {formatDuration(r.onSiteSeconds)} · Total {formatDuration(r.totalSeconds)}
          </Row>
        </tbody>
      </table>

      {section("Report from the unit")}
      <div style={{ fontSize: "11px", whiteSpace: "pre-wrap", marginBottom: "4px" }}>{r.unitRemarks || "—"}</div>
      {detail.checklist.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {detail.checklist.map((c) => (
              <tr key={c.checklistItemId}>
                <td style={{ ...PRINT_TD, width: "20px" }}>{c.isChecked ? "✔" : "✘"}</td>
                <td style={{ ...PRINT_TD, width: "200px" }}>{c.itemName}</td>
                <td style={PRINT_TD}>{c.remarks || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {section("Control room")}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <Row label="Action taken">{r.actionTaken || "—"}</Row>
          {r.status === "CONVERTED" && <Row label="Pushed to Tech">{r.technicalWorkNumber} ({r.technicalWorkStatus}) · {r.convertedByName} · {formatDateTime(r.convertedDate)}</Row>}
          {r.status === "CLOSED" && <Row label="Closed">{r.closedByName} · {formatDateTime(r.closedDate)}</Row>}
          {r.status === "CANCELLED" && <Row label="Cancelled">{r.cancelReason} · {r.cancelledByName} · {formatDateTime(r.cancelledDate)}</Row>}
        </tbody>
      </table>

      {photoUrls.length > 0 && (
        <>
          {section(`Photos (${photoUrls.length})`)}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {photoUrls.map((src, i) => (
              <img key={i} src={src} alt="" style={{ width: "32%", border: "1px solid #999", pageBreakInside: "avoid" }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Prints the one-alarm sheet. Pass `detail` when the page already has it (detail page), or
 * just `recordGuid` to fetch it on click (a row of the report list). `compact` renders an
 * icon-only button for table rows.
 */
const AlarmSheetPrintButton = ({ detail, recordGuid, compact = false, icon: Icon = Printer }) => {
  const [fetchPhoto] = useLazyGetAlarmPhotoQuery();
  const [fetchDetail] = useLazyGetAlarmResponseQuery();
  const [busy, setBusy] = useState(false);

  const print = async () => {
    // Opened now, while this is still the click: a window opened after the awaits below
    // can be treated as an unrequested pop-up and blocked.
    const win = window.open("", "_blank", "width=1100,height=900");
    if (!win) {
      toast.error("Allow pop-ups for this site to print.");
      return;
    }
    win.document.write("<p style='font-family:Arial;padding:16px'>Preparing the report...</p>");
    setBusy(true);
    try {
      const data = detail || (await fetchDetail(recordGuid, true).unwrap());
      const r = data.response;
      // The popup cannot send our auth header, so photos travel inside the page as data URLs.
      const photoUrls = await Promise.all(
        data.photos.map(async (p) =>
          blobToDataUrl(await fetchPhoto({ recordGuid: r.recordGuid, photoGuid: p.recordGuid }, true).unwrap())
        )
      );
      printElement(<AlarmSheet detail={data} photoUrls={photoUrls} />, { title: r.responseNumber, win });
    } catch (e) {
      win.close();
      toast.error(e?.message || "Could not prepare the print");
    } finally {
      setBusy(false);
    }
  };

  if (compact) {
    return (
      <button type="button" className="btn btn-sm btn-outline-secondary p-1 lh-1" onClick={print} disabled={busy} title="Print this response">
        {busy ? <span className="spinner-border spinner-border-sm" /> : <Icon size={14} />}
      </button>
    );
  }

  return (
    <button type="button" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1" onClick={print} disabled={busy}>
      <Icon size={14} /> {busy ? "Preparing..." : "Print"}
    </button>
  );
};

export default AlarmSheetPrintButton;
